import {
    Component,
    ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CreepsService } from '../creeps.service';

import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthService, ApiService, LocalStorageService } from '../../core/upgraded-providers';

import { AlertService, ConfirmService } from '../../ui.module';

const MSG_ALERT_DELETE = 'You cannot delete a Power Creep which is spawned in the world';
const { NOT_LOSE_POWER_LEVEL_END_TIME } = window.CONFIG;

@Component({
    selector: 'app-power-creeps',
    templateUrl: './creeps.component.pug',
    styleUrls: ['./creeps.component.less'],
    encapsulation: ViewEncapsulation.None
})
export class CreepsComponent {
    public _expanded = this._localStorage.get('powerCreepsExpanded', true);
    public get expanded() {
        return this._expanded;
    }

    public set expanded(value) {
        this._expanded = value;
        this._localStorage.put('powerCreepsExpanded', value);
    }

    public creeps: IPowerCreep[] = [];

    public isRefreshed = false;
    private _confirmsDelete = {};
    private _confirmsRename = {};

    public NOT_LOSE_POWER_LEVEL_END_TIME = NOT_LOSE_POWER_LEVEL_END_TIME;

    private _powerLevelSbj = new BehaviorSubject<any>(null);
    public powerLevel$: Observable<number>;

    private isOfficialServer = false;

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _localStorage: LocalStorageService,
        private _authService: AuthService,
        private _creepsService: CreepsService,
        private _apiService: ApiService,
        private _alert: AlertService,
        private _confirm: ConfirmService
    ) {
        this.creeps = this._route.snapshot.data['creeps'];

        this.powerLevel$ = this._powerLevelSbj.asObservable()
            .pipe(map((creeps) => creeps.reduce((powerLevel, { level }) => {
                return powerLevel + level + 1;
            }, 0)));

        this._powerLevelSbj.next(this.creeps);

        this.isOfficialServer = this._apiService.options.official;
    }

    public getPowersKeys(powers) {
        return Object.keys(powers);
    }

    private async _readList() {
        const { list } = await this._creepsService.read();
        this.creeps = list;
        this._route.snapshot.data['creeps'] = this.creeps;
        this._powerLevelSbj.next(this.creeps);
    }

    public onCreate() {
        this._router.navigate(['/overview/power', 'new'], {
            relativeTo: this._route
        });
    }

    public isSpawned({ room, shard }: IPowerCreep) {
        if (this.isOfficialServer && room && shard) {
            return true;
        }

        if (this.isOfficialServer && room) {
            return true;
        }

        return false;
    }

    public onUpgrade({ _id }) {
        this._router.navigate(['/overview/power', _id], {
            relativeTo: this._route
        });
    }

    public isConfirmRename({ _id }): string {
        return this._confirmsRename[_id];
    }

    public onConfirmRename({ _id }, name): void {
        this._confirmsRename[_id] = name;
    }

    public async onRename({ _id: id }, next) {
        const name = this._confirmsRename[id];

        await this._creepsService.rename({ id, name });
        await this._readList();

        this.onConfirmRename({ _id: id }, null);

        next();
    }

    public isConfirmDelete({ _id }): boolean {
        return this._confirmsDelete[_id];
    }

    public onConfirmDelete(creep: IPowerCreep): void {
        if (this.isSpawned(creep)) {
            this._showAlertDialog(MSG_ALERT_DELETE);
            return;
        }

        this._confirmsDelete[creep._id] = !this._confirmsDelete[creep._id];
    }

    public async onDelete(creep: IPowerCreep, next: Function) {
        await this._creepsService.delete({ id: creep._id });
        await this._readList();

        this.onConfirmDelete(creep);

        next();
    }

    public async onCancelDeletion({ _id: id }, next) {
        await this._creepsService.cancelDeletion({ id });
        await this._readList();

        next();
    }

    public async onEndDeleteTime({ _id }) {
        const idx = this.creeps.findIndex((creep) => creep._id === _id);
        this.creeps[idx] = Object.assign({}, this.creeps[idx]);
    }

    public async onRefresh() {
        this.isRefreshed = true;
        await this._readList();
        this.isRefreshed = false;
    }

    public hasSomeSpawned(): boolean {
        return this.creeps.some((creep) => this.isSpawned(creep));
    }

    public async onActivatePowerExperimentation() {
        try {
            await this._showActivatePowerExperimentationDialog();
            await this._creepsService.activatePowerExperimentation();
            await this._authService.check();
        } catch (err) {
            // Noop.
        }
    }

    public onActivate(creepTool) {
        creepTool.save.subscribe(async (...args) => await this._readList());

        const { creepId } = this._route.firstChild.snapshot.params;

        if (creepId === 'new') {
            return;
        }

        const creep = this.creeps.find(({ _id }) => creepId === _id);
        creepTool.setCreep({
            ...creep,
            powers: { ...creep.powers }
        });
    }

    private _showAlertDialog(message) {
        this._alert.show({ data: {
            message
        }});
    }

    private _showActivatePowerExperimentationDialog(): Promise<any> {
        return this._confirm.show({ data: {
            innerHTML: '\
                <div class=\'--text-left\'>\
                You can activate a 24-hour experimentation period to work on your Power Creeps builds without losing levels. \
                During an experimentation period:\
                <br/><br/>\
                <ul>\
                    <li>Power Creeps are deleted immediately without delay.</li>\
                    <li>You don\'t lose Global Power Levels when you delete a Power Creep.</li>\
                </ul>\
                <br/>' +
                `You have ${ this._authService.Me.powerExperimentations || 0 } periods left. Would you like to activate it?` +
                '</div>',
            buttonOkLabel: 'Activate'
        }});
    }
}
